/**
 * The admin decisions on an organization, as the tenant home's overflow
 * menu: disable it, issue the one-time token that an admin redeems to bring
 * it back, or delete it once disabled. Each confirms first; the token is
 * shown once, in a dialog, as every one-time secret is.
 *
 * The UI says enable/disable; the API routes stay deactivate/reactivate-
 * initiate, which is what the names below call.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import {
  deactivateOrganization,
  deleteOrganization,
  reactivateInitiate,
} from '@optum/api-client/endpoints/organizations';

import { Button } from '../../ui/Button.js';
import { useConfirm } from '../../ui/Confirm.js';
import { Dialog, DialogActions } from '../../ui/Dialog.js';
import { Dropdown, DropdownItem } from '../../ui/Dropdown.js';
import { CopyButton, IconButton } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';

export function OrgActions({ id, name, status }: { id: string; name: string; status: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [token, setToken] = useState<string | null>(null);
  const isDisabled = status === 'inactive';

  const disable = useMutation({
    mutationFn: () => deactivateOrganization(id),
    onSuccess: () => {
      toast.success('Disabled');
      qc.invalidateQueries({ queryKey: qk.org(id) });
      qc.invalidateQueries({ queryKey: qk.orgs() });
    },
    onError: (err) =>
      toast.error('Disable failed', err instanceof ApiError ? err.message : String(err)),
  });

  const enable = useMutation({
    mutationFn: () => reactivateInitiate(id),
    onSuccess: (out) => {
      setToken(out.token);
      toast.success('Enable token issued');
    },
    onError: (err) =>
      toast.error('Enable failed', err instanceof ApiError ? err.message : String(err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteOrganization(id),
    onSuccess: () => {
      toast.success('Organization deleted');
      qc.invalidateQueries({ queryKey: qk.orgs() });
      navigate('/organizations', { replace: true });
    },
    onError: (err) =>
      toast.error('Delete failed', err instanceof ApiError ? err.message : String(err)),
  });

  function ask(title: string, description: string, confirmLabel: string, run: () => void): void {
    void confirm({ title, description, confirmLabel, tone: 'danger' }).then((ok) => {
      if (ok) run();
    });
  }

  return (
    <>
      <Dropdown
        align="end"
        trigger={<IconButton icon="more_horiz" size={34} aria-label="Organization actions" />}
      >
        {!isDisabled ? (
          <DropdownItem
            icon="block"
            label="Disable"
            sub="Members lose access until an enable token is redeemed"
            tone="danger"
            disabled={disable.isPending}
            onSelect={() =>
              ask(
                'Disable this organization?',
                `${name} will be set to disabled. Members lose access until you issue an enable token.`,
                'Disable',
                () => disable.mutate(),
              )
            }
          />
        ) : (
          <>
            <DropdownItem
              icon="key"
              label="Issue enable token"
              sub="One-time, valid 72 h"
              disabled={enable.isPending}
              onSelect={() =>
                ask(
                  'Issue an enable token?',
                  `Generates a one-time token, valid 72 hours, that an admin of ${name} can redeem to set the organization back to active.`,
                  'Issue token',
                  () => enable.mutate(),
                )
              }
            />
            <DropdownItem
              icon="delete"
              label="Delete permanently"
              sub="Memberships, secrets, and configuration go with it"
              tone="danger"
              disabled={remove.isPending}
              onSelect={() =>
                ask(
                  'Delete this organization?',
                  `${name} will be permanently removed along with its memberships, secrets, and capability configuration. This cannot be undone.`,
                  'Delete',
                  () => remove.mutate(),
                )
              }
            />
          </>
        )}
      </Dropdown>
      <Dialog
        open={token !== null}
        onClose={() => setToken(null)}
        title="Save this enable token now"
        subtitle="It is shown once. An admin of the organization redeems it within 72 hours."
      >
        <div
          style={{
            marginTop: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-sunken)',
            padding: 11,
          }}
        >
          <code style={{ wordBreak: 'break-all', fontSize: 'var(--text-meta-lg)', flex: 1 }}>
            {token}
          </code>
          {token ? <CopyButton value={token} /> : null}
        </div>
        <DialogActions>
          <Button variant="primary" onClick={() => setToken(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
